import { GridColDef, GridRowParams, GridValidRowModel } from '@mui/x-data-grid'
import { Dispatch, SetStateAction, useMemo } from 'react'
import * as R from 'remeda'
import { ModelListRowModel, Warning } from './types'
import { StatusChip } from './components/StatusChip'
import { createShopifyProductUrl } from '~/features/shopify/utils'
import { TextLink } from '~/ui/components/TextLink'
import { FilterSelect } from '~/features/filtering/components/FilterSelect'
import { FilterString } from '~/features/filtering/components/FilterString'
import { FilterNumber } from '~/features/filtering/components/FilterNumber'
import { FilterMultiSelect } from '~/features/filtering/components/FilterMultiSelect'
import { IconButton, SvgIcon, Tooltip } from '@mui/material'
import { IconAlertCircle, IconListDetails } from '@tabler/icons-react'
import { ACTION_ROW_CLASSNAME } from '~/ui/styles/data-grid'
import { modelStatusMap, modelStatuses } from '~/utils/constants'
import { format } from 'date-fns'

type TypedGridColDef<T extends GridValidRowModel> = GridColDef<T> & {
  customFilter?: { Component: React.ReactNode }
}

const warningFilters: Array<{
  value: Warning['key']
  label: string
}> = [
  {
    value: 'inconsistent_shopify_product_id',
    label: 'Inconsistent Shopify product id',
  },
  {
    value: 'inconsistent_shopify_price',
    label: 'Inconsistent Shopify price',
  },
  {
    value: 'missing_shopify_size',
    label: 'Missing Shopify size',
  },
  {
    value: 'missing_images',
    label: 'Missing images',
  },
  {
    value: 'inconsistent_shopify_quantity',
    label: 'Inconsistent Shopify quantity',
  },
  {
    value: 'missing_shopify_variants',
    label: 'Missing Shopify variants',
  },
  {
    value: 'inconsistent_product_variant_id',
    label: 'Inconsistent product variant id',
  },
  {
    value: 'inconsistent_inventory_item_id',
    label: 'Inconsistent inventory item id',
  },
  {
    value: 'inconsistent_shopify_variant_id',
    label: 'Inconsistent Shopify variant id',
  },
  {
    value: 'model_missing_in_inventory',
    label: 'Model missing in inventory',
  },
]

export type FilterOperator = 'equals' | 'lessThan' | 'greaterThan'

export type FilterCondition = {
  setter: (rows: ModelListRowModel[]) => ModelListRowModel[]
  id: string
  label: string
  value: unknown
  operator?: FilterOperator
}

type UseModelListColumnsProps = {
  filters: FilterCondition[]
  setFilters: Dispatch<SetStateAction<FilterCondition[]>>
  isInitiallyLoading: boolean
  hideFilterComponent: () => void
  setDrawerAnchorId: (id: number) => void
}

export const useModelListColumns = ({
  filters,
  setFilters,
  isInitiallyLoading,
  hideFilterComponent,
  setDrawerAnchorId,
}: UseModelListColumnsProps) => {
  return useMemo<TypedGridColDef<ModelListRowModel>[]>(() => {
    return [
      {
        resizable: false,
        field: 'actions',
        type: 'actions',
        width: 40,
        getActions: (params: GridRowParams<ModelListRowModel>) => {
          return [
            <IconButton
              key={params.id}
              className={ACTION_ROW_CLASSNAME}
              sx={{ color: 'text.secondary', textTransform: 'none', display: 'none' }}
              size="small"
              onClick={() => setDrawerAnchorId(params.row.id)}
            >
              <SvgIcon fontSize="small">
                <IconListDetails />
              </SvgIcon>
            </IconButton>,
          ]
        },
      },
      {
        customFilter: {
          Component: (
            <FilterMultiSelect
              label="Statuses"
              defaultValue={R.pipe(
                filters,
                R.find((f) => f.id === 'status_v2'),
                R.prop('value'),
                (value) => (Array.isArray(value) ? value : undefined)
              )}
              options={modelStatuses.map((status) => ({
                value: status,
                label: modelStatusMap[status].label,
              }))}
              onSubmit={({ value, label }) => {
                setFilters((prev) => [
                  ...prev.filter((f) => f.id !== 'status_v2'),
                  {
                    id: 'status_v2',
                    label,
                    value,
                    setter: (rows) => rows.filter((row) => value.includes(row.status_v2)),
                  },
                ])

                hideFilterComponent()
              }}
              onClose={hideFilterComponent}
            />
          ),
        },
        filterable: false,
        field: 'status_v2',
        headerName: 'Status',
        width: 120,
        renderCell: (params) => {
          return (
            <StatusChip
              status={{ type: 'model', value: params.row.status_v2 }}
              isLoading={isInitiallyLoading}
              warnings={params.row.warnings}
            />
          )
        },
        valueGetter: (_, row) => {
          return modelStatusMap[row.status_v2].label
        },
      },
      {
        customFilter: {
          Component: (
            <FilterString
              label="Stock code contains"
              defaultValue={R.pipe(
                filters,
                R.find((f) => f.id === 'stock_code'),
                R.prop('value'),
                (value) => (typeof value === 'string' ? value : undefined)
              )}
              onSubmit={({ value, label }) => {
                setFilters((prev) => [
                  ...prev.filter((f) => f.id !== 'stock_code'),
                  {
                    id: 'stock_code',
                    label,
                    value,
                    setter: (rows) =>
                      rows.filter((row) =>
                        row.stock_code.toLowerCase().includes(value.toLowerCase())
                      ),
                  },
                ])

                hideFilterComponent()
              }}
              onClose={hideFilterComponent}
              placeholder="Enter stock code..."
            />
          ),
        },
        field: 'stock_code',
        headerName: 'Stock code',
        width: 120,
        filterable: false,
      },
      {
        customFilter: {
          Component: (
            <FilterNumber
              label="Price"
              defaultValue={R.pipe(
                filters,
                R.find((f) => f.id === 'price'),
                (filter) =>
                  filter
                    ? {
                        value: filter.value as number,
                        operator: filter.operator ?? 'equals',
                      }
                    : undefined
              )}
              onSubmit={({ value, label, operator, setter }) => {
                setFilters((prev) => [
                  ...prev.filter((f) => f.id !== 'price'),
                  {
                    id: 'price',
                    label,
                    value,
                    operator,
                    setter: (rows) => rows.filter((row) => setter(row.price)),
                  },
                ])

                hideFilterComponent()
              }}
              onClose={hideFilterComponent}
              placeholder="Enter price..."
            />
          ),
        },
        field: 'price',
        headerName: 'Price',
        width: 100,
        type: 'number',
        filterable: false,
      },
      {
        customFilter: {
          Component: (
            <FilterNumber
              label="Shopify quantity"
              defaultValue={R.pipe(
                filters,
                R.find((f) => f.id === 'shopifyQuantity'),
                (filter) =>
                  filter
                    ? {
                        value: filter.value as number,
                        operator: filter.operator ?? 'equals',
                      }
                    : undefined
              )}
              onSubmit={({ value, label, operator, setter }) => {
                setFilters((prev) => [
                  ...prev.filter((f) => f.id !== 'shopifyQuantity'),
                  {
                    id: 'shopifyQuantity',
                    label,
                    value,
                    operator,
                    setter: (rows) => rows.filter((row) => setter(row.shopifyQuantity)),
                  },
                ])

                hideFilterComponent()
              }}
              onClose={hideFilterComponent}
              placeholder="Enter quantity..."
            />
          ),
        },
        field: 'shopifyQuantity',
        headerName: 'Shopify quantity',
        type: 'number',
        filterable: false,
      },
      {
        customFilter: {
          Component: (
            <FilterNumber
              label="Available"
              defaultValue={R.pipe(
                filters,
                R.find((f) => f.id === 'availableInventory'),
                (filter) =>
                  filter
                    ? {
                        value: filter.value as number,
                        operator: filter.operator ?? 'equals',
                      }
                    : undefined
              )}
              onSubmit={({ value, label, operator, setter }) => {
                setFilters((prev) => [
                  ...prev.filter((f) => f.id !== 'availableInventory'),
                  {
                    id: 'availableInventory',
                    label,
                    value,
                    operator,
                    setter: (rows) => rows.filter((row) => setter(row.availableInventory)),
                  },
                ])

                hideFilterComponent()
              }}
              onClose={hideFilterComponent}
              placeholder="Enter available quantity..."
            />
          ),
        },
        field: 'availableInventory',
        headerName: 'Available',
        type: 'number',
        filterable: false,
      },
      {
        customFilter: {
          Component: (
            <FilterNumber
              label="Sold"
              defaultValue={R.pipe(
                filters,
                R.find((f) => f.id === 'soldInventory'),
                (filter) =>
                  filter
                    ? {
                        value: filter.value as number,
                        operator: filter.operator ?? 'equals',
                      }
                    : undefined
              )}
              onSubmit={({ value, label, operator, setter }) => {
                setFilters((prev) => [
                  ...prev.filter((f) => f.id !== 'soldInventory'),
                  {
                    id: 'soldInventory',
                    label,
                    value,
                    operator,
                    setter: (rows) => rows.filter((row) => setter(row.soldInventory)),
                  },
                ])

                hideFilterComponent()
              }}
              onClose={hideFilterComponent}
              placeholder="Enter sold quantity..."
            />
          ),
        },
        field: 'soldInventory',
        headerName: 'Sold',
        type: 'number',
        filterable: false,
      },
      {
        field: 'shopify_product_id',
        headerName: 'Shopify product id',
        width: 150,
        renderCell: (params) => {
          return params.row.shopify_product_id ? (
            <TextLink
              to={createShopifyProductUrl(params.row.shopify_product_id)}
              label="Go to Shopify"
              target="_blank"
            />
          ) : null
        },
        filterable: false,
      },
      {
        customFilter: {
          Component: (
            <FilterSelect
              label="Warnings include"
              defaultValue={R.pipe(
                filters,
                R.find((f) => f.id === 'warnings'),
                R.prop('value'),
                (value) => warningFilters.find((w) => w.value === value)?.value
              )}
              options={warningFilters}
              onSubmit={({ value, label }) => {
                setFilters((prev) => [
                  ...prev.filter((f) => f.id !== 'warnings'),
                  {
                    id: 'warnings',
                    label,
                    value,
                    setter: (rows) =>
                      rows.filter(
                        (row) =>
                          row.warnings && row.warnings.some((warning) => warning.key === value)
                      ),
                  },
                ])

                hideFilterComponent()
              }}
              onClose={hideFilterComponent}
            />
          ),
        },
        field: 'warnings',
        filterable: false,
        headerName: 'Warnings',
        type: 'boolean',
        valueGetter: (_, row) => {
          return !!row.warnings?.length
        },
        renderCell: (params) => {
          // Don't render during loading state (skeleton rows)
          if (isInitiallyLoading) return null

          return params.row.warnings?.length ? (
            <Tooltip title={params.row.warnings.map((warning) => warning.message).join('\n')}>
              <SvgIcon fontSize="small" color="warning">
                <IconAlertCircle />
              </SvgIcon>
            </Tooltip>
          ) : null
        },
      },
      {
        field: 'updated_at',
        filterable: false,
        headerName: 'Updated At',
        valueGetter: (value) => (value ? format(value, 'dd MMM yyyy') : ''),
      },
      {
        field: 'created_at',
        filterable: false,
        headerName: 'Created At',
        valueGetter: (value) => (value ? format(value, 'dd MMM yyyy') : ''),
      },
      {
        field: 'id',
        headerName: 'Id',
        customFilter: {
          Component: (
            <FilterNumber
              label="Id"
              defaultValue={R.pipe(
                filters,
                R.find((f) => f.id === 'id'),
                (filter) =>
                  filter && R.isNumber(filter.value)
                    ? {
                        value: filter.value,
                        operator: filter.operator ?? 'equals',
                      }
                    : undefined
              )}
              onSubmit={({ value, label, operator, setter }) => {
                setFilters((prev) => [
                  ...prev.filter((f) => f.id !== 'id'),
                  {
                    id: 'id',
                    label,
                    value,
                    operator,
                    setter: (rows) => rows.filter((row) => setter(row.id)),
                  },
                ])

                hideFilterComponent()
              }}
              onClose={hideFilterComponent}
              placeholder="Enter id..."
            />
          ),
        },
      },
    ]
  }, [filters, isInitiallyLoading, hideFilterComponent, setFilters, setDrawerAnchorId])
}
